/**
 * How the goal list is organised (Goals UX pass 2026-09-06). A goal list is
 * read for "which goals are moving, which one needs a next step from me, and
 * how far along is each", so the default grouping is by movement:
 *
 *   Ready to check    an open review from the persona, the journey is complete, or the date
 *                     you set has passed — answer it
 *   In motion         a next thing is set, something got done under it in the last fourteen days,
 *                     or it is under way
 *   Needs a journey   live, nothing under it, no journey — the next move is to plan one
 *   Waiting for you   has items or a journey, but nothing next and nothing recent
 *   Paused            paused by the person
 *   Done              done, and let go — collapsed
 *
 * One status axis reduces to words with a precedence: Done › Let go › Paused ›
 * Parked › nothing. Parked is rationing's word, not the person's choice: it
 * shows as a tag but the goal stays live (the same call as skills).
 *
 * Pure functions with no runtime imports so node can probe them
 * (`npm run test:goals`); the page injects the life-area labeller.
 */
export type GoalSectionKey = 'check' | 'moving' | 'start' | 'waiting' | 'paused' | 'done'
export type GoalGroupBy = 'movement' | 'dream' | 'area' | 'none'
export type GoalSortBy = 'attention' | 'progress' | 'created' | 'updated'
export type SortDir = 'asc' | 'desc'
export interface SortSpec { by: GoalSortBy; dir: SortDir }

export interface GoalLike {
  id: number | string
  title: string
  status?: string | null
  progress?: number | null
  target_date?: string | null
  completed_at?: string | null
  let_go_at?: string | null
  created_at?: string | null
  updated_at?: string | null
  life_area?: string | null
  parent?: { type: string; id: number; title: string | null; life_area?: string | null } | null
}

export interface GoalCardLike {
  path?: { status: string; done: number; total: number; current?: unknown } | null
  next?: { date?: string | null } | null
  today?: unknown | null
  days_active?: number
  window_days?: number
  window_capped?: boolean
  days_total?: number
  last_date?: string | null
  review?: unknown | null
  open_tasks?: number
  items?: { tasks?: unknown[]; habits?: unknown[]; skills?: unknown[] } | null
}

export interface GoalGroup<G extends GoalLike = GoalLike> {
  key: string
  title: string
  quiet?: string
  items: G[]
  collapsedByDefault?: boolean
}

export const SECTION_TITLE: Record<GoalSectionKey, string> = {
  check: 'Ready to check',
  moving: 'In motion',
  start: 'Needs a journey',
  waiting: 'Waiting for you',
  paused: 'Paused',
  done: 'Done',
}
const SECTION_ORDER: GoalSectionKey[] = ['check', 'moving', 'start', 'waiting', 'paused', 'done']

// ── one status axis, one line of words ──

export type GoalStateKey = 'done' | 'let_go' | 'paused' | 'parked' | 'active'
export interface GoalState { key: GoalStateKey; words: string | null }

export function goalState(g: GoalLike): GoalState {
  const s = String(g.status || '').toLowerCase()
  if (s === 'completed') return { key: 'done', words: 'Done' }
  if (s === 'cancelled' || s === 'dropped' || s === 'lapsed') return { key: 'let_go', words: 'Let go' }
  if (s === 'paused' || s === 'paused_by_user' || s === 'hold') return { key: 'paused', words: 'Paused' }
  if (s === 'parked') return { key: 'parked', words: 'Parked' }
  return { key: 'active', words: null }
}

export const isFinished = (g: GoalLike): boolean => { const k = goalState(g).key; return k === 'done' || k === 'let_go' }
export const isResting = (g: GoalLike): boolean => goalState(g).key === 'paused'

/** Whole local days from one YYYY-MM-DD to another; null when unreadable. */
export function daysBetween(fromKey: string, toKey: string): number | null {
  const a = /^(\d{4})-(\d{2})-(\d{2})/.exec(fromKey), b = /^(\d{4})-(\d{2})-(\d{2})/.exec(toKey)
  if (!a || !b) return null
  const da = new Date(Number(a[1]), Number(a[2]) - 1, Number(a[3]))
  const db = new Date(Number(b[1]), Number(b[2]) - 1, Number(b[3]))
  return Math.round((db.getTime() - da.getTime()) / 86_400_000)
}

export function targetPassed(g: GoalLike, todayKey: string): boolean {
  if (!g.target_date) return false
  const d = daysBetween(todayKey, g.target_date)
  return d != null && d < 0
}

/** Anything under the goal at all — a task, a habit, a skill, or a journey. */
export function hasAnything(card: GoalCardLike | null | undefined): boolean {
  if (!card) return false
  if (card.path) return true
  const it = card.items
  return Boolean((it?.tasks?.length || 0) + (it?.habits?.length || 0) + (it?.skills?.length || 0) || card.days_total)
}

export function sectionOf(g: GoalLike, card: GoalCardLike | null | undefined, todayKey: string): GoalSectionKey {
  const state = goalState(g)
  if (state.key === 'done' || state.key === 'let_go') return 'done'
  if (state.key === 'paused') return 'paused'
  if (card?.review || card?.path?.status === 'completed' || targetPassed(g, todayKey)) return 'check'
  if (card?.today || card?.next) return 'moving'
  if (card?.last_date) {
    const d = daysBetween(card.last_date, todayKey)
    if (d != null && d <= 14) return 'moving'
  }
  if (String(g.status || '').toLowerCase() === 'in_progress' && hasAnything(card)) return 'moving'
  if (!hasAnything(card)) return 'start'
  return 'waiting'
}

const cmp = (a: string | number, b: string | number) => (a < b ? -1 : a > b ? 1 : 0)
const title = (g: GoalLike) => g.title.toLowerCase()
const pct = (g: GoalLike) => Number(g.progress || 0)

export function sortSection<G extends GoalLike>(items: G[], key: GoalSectionKey, cards: Record<string, GoalCardLike | undefined>): G[] {
  const card = (g: G) => cards[String(g.id)]
  const arr = [...items]
  switch (key) {
    case 'moving':
      return arr.sort((a, b) =>
        cmp(card(a)?.today ? 0 : 1, card(b)?.today ? 0 : 1)
        || cmp(String(card(a)?.next?.date || '9999'), String(card(b)?.next?.date || '9999'))
        || cmp(String(card(b)?.last_date || ''), String(card(a)?.last_date || ''))
        || cmp(title(a), title(b)))
    case 'waiting':
      return arr.sort((a, b) => cmp(String(card(b)?.last_date || ''), String(card(a)?.last_date || '')) || cmp(pct(b), pct(a)) || cmp(title(a), title(b)))
    case 'start':
      return arr.sort((a, b) => cmp(String(b.created_at || ''), String(a.created_at || '')))
    case 'done':
      return arr.sort((a, b) => cmp(String(b.completed_at || b.let_go_at || b.updated_at || ''), String(a.completed_at || a.let_go_at || a.updated_at || '')))
    default:
      return arr.sort((a, b) => cmp(title(a), title(b)))
  }
}

export function timeKey(g: GoalLike, by: 'created' | 'updated'): string {
  return String((by === 'created' ? (g.created_at || g.updated_at) : (g.updated_at || g.created_at)) || '')
}

export function sortByTime<G extends GoalLike>(items: G[], by: 'created' | 'updated', dir: SortDir): G[] {
  return [...items].sort((a, b) => {
    const ka = timeKey(a, by), kb = timeKey(b, by)
    if (!ka || !kb) return cmp(ka ? 0 : 1, kb ? 0 : 1)
    return dir === 'desc' ? cmp(kb, ka) : cmp(ka, kb)
  })
}

export function sortByProgress<G extends GoalLike>(items: G[], dir: SortDir): G[] {
  return [...items].sort((a, b) => (dir === 'desc' ? cmp(pct(b), pct(a)) : cmp(pct(a), pct(b))) || cmp(title(a), title(b)))
}

export function matchesQuery(g: GoalLike & { description?: string | null; life_areas?: string[] | null; success_criteria?: string | null }, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  const hay = [g.title, g.description, g.success_criteria, g.parent?.title, g.life_area, ...(g.life_areas || [])].filter(Boolean).join(' ').toLowerCase()
  return q.split(/\s+/).every((w) => hay.includes(w))
}

export interface GoalSummary { total: number; open: number; moving: number; check: number; doneWeek: number; resting: number; done: number }

export function summarize(goals: GoalLike[], cards: Record<string, GoalCardLike | undefined>, todayKey: string): GoalSummary {
  const s: GoalSummary = { total: 0, open: 0, moving: 0, check: 0, doneWeek: 0, resting: 0, done: 0 }
  for (const g of goals) {
    s.total += 1
    const k = sectionOf(g, cards[String(g.id)], todayKey)
    if (k === 'done') s.done += 1; else s.open += 1
    if (k === 'moving') s.moving += 1
    if (k === 'check') s.check += 1
    if (k === 'paused') s.resting += 1
    const last = cards[String(g.id)]?.last_date
    if (last && k !== 'done') { const d = daysBetween(last, todayKey); if (d != null && d >= 0 && d <= 6) s.doneWeek += 1 }
  }
  return s
}

export function summaryWords(s: GoalSummary): string {
  if (s.total === 0) return 'Nothing here yet'
  const parts = [`${s.open} open`]
  if (s.check) parts.push(`${s.check} ready to check`)
  if (s.moving) parts.push(`${s.moving} in motion`)
  if (s.doneWeek) parts.push(`${s.doneWeek} with something done this week`)
  if (s.resting) parts.push(`${s.resting} paused`)
  if (s.done) parts.push(`${s.done} done`)
  return parts.join(' · ')
}

const attentionOrder = <G extends GoalLike>(cards: Record<string, GoalCardLike | undefined>, todayKey: string) => (a: G, b: G) => {
  const ka = sectionOf(a, cards[String(a.id)], todayKey), kb = sectionOf(b, cards[String(b.id)], todayKey)
  if (ka !== kb) return cmp(SECTION_ORDER.indexOf(ka), SECTION_ORDER.indexOf(kb))
  return sortSection([a, b], ka, cards)[0] === a ? -1 : 1
}

export function groupGoals<G extends GoalLike>(
  goals: G[], cards: Record<string, GoalCardLike | undefined>, by: GoalGroupBy, todayKey: string,
  labelArea: (value: string) => string = (v) => v, sort: SortSpec = { by: 'attention', dir: 'desc' },
): GoalGroup<G>[] {
  const groups = buildGroups(goals, cards, by, todayKey, labelArea)
  if (sort.by === 'attention') return groups
  if (sort.by === 'progress') return groups.map((g) => ({ ...g, items: sortByProgress(g.items, sort.dir) }))
  return groups.map((g) => ({ ...g, items: sortByTime(g.items, sort.by as 'created' | 'updated', sort.dir) }))
}

function buildGroups<G extends GoalLike>(goals: G[], cards: Record<string, GoalCardLike | undefined>, by: GoalGroupBy, todayKey: string, labelArea: (v: string) => string): GoalGroup<G>[] {
  if (by === 'movement') {
    const buckets: Record<GoalSectionKey, G[]> = { check: [], moving: [], start: [], waiting: [], paused: [], done: [] }
    for (const g of goals) buckets[sectionOf(g, cards[String(g.id)], todayKey)].push(g)
    const anyLive = (['check', 'start', 'waiting'] as GoalSectionKey[]).some((k) => buckets[k].length > 0)
    return SECTION_ORDER
      .map((k): GoalGroup<G> => ({
        key: k, title: SECTION_TITLE[k], items: sortSection(buckets[k], k, cards),
        quiet: k === 'moving' && buckets.moving.length === 0 && anyLive ? 'Nothing in motion right now. Plan a journey on one, or finish one small thing under it.' : undefined,
        collapsedByDefault: k === 'done',
      }))
      .filter((g) => g.items.length > 0 || g.quiet)
  }
  const live = goals.filter((g) => !isFinished(g))
  const finished = sortSection(goals.filter(isFinished), 'done', cards)
  if (by === 'none') {
    const groups: GoalGroup<G>[] = []
    if (live.length) groups.push({ key: 'all', title: 'Goals', items: [...live].sort(attentionOrder(cards, todayKey)) })
    if (finished.length) groups.push({ key: 'done', title: SECTION_TITLE.done, items: finished, collapsedByDefault: true })
    return groups
  }
  const map = new Map<string, GoalGroup<G>>()
  const orphanKey = by === 'dream' ? 'none' : 'area:none'
  for (const g of live) {
    let key = orphanKey
    let t = by === 'dream' ? 'On their own' : 'No life area'
    if (by === 'dream' && g.parent?.title) { key = `${g.parent.type}:${g.parent.id}`; t = g.parent.title }
    if (by === 'area') {
      const area = g.life_area || g.parent?.life_area || null
      if (area) { key = `area:${area}`; t = labelArea(area) }
    }
    if (!map.has(key)) map.set(key, { key, title: t, items: [] })
    map.get(key)!.items.push(g)
  }
  const groups = [...map.values()]
  for (const g of groups) g.items.sort(attentionOrder(cards, todayKey))
  groups.sort((a, b) => cmp(a.key === orphanKey ? 1 : 0, b.key === orphanKey ? 1 : 0) || cmp(a.title.toLowerCase(), b.title.toLowerCase()))
  if (finished.length) groups.push({ key: 'done', title: SECTION_TITLE.done, items: finished, collapsedByDefault: true })
  return groups
}
